/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useParticipants } from "@livekit/components-react";
import {
  MatrixEventEvent,
  RoomStateEvent,
  type MatrixEvent,
  type Room as MatrixRoom,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import { type Room } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useClient } from "../ClientContext.tsx";
import { getHomeserverAssociatedDomain } from "../knownHomeserversMap.ts";
import {
  ProtocolistStatusEventType,
  ProtocolistStatusStateKey,
  type ProtocolistStatusEventContent,
  isProtocolistStatusEventContent,
} from "./protocolistEvent.ts";
import { callTraceJournal, describeTraceError } from "../telemetry";

export type ProtocolistRecipientsMode =
  | "owner"
  | "all_participants"
  | "self"
  | "selected";

export interface ProtocolistRecipientsPolicy {
  mode: ProtocolistRecipientsMode;
  selected_user_ids: string[];
  self_user_id: string | null;
  updated_by: string | null;
  updated_at: number | null;
}

export interface ProtocolistRecipientsOption {
  mode: ProtocolistRecipientsMode;
  label: string;
}

export interface ProtocolistRecipientsCandidate {
  user_id: string;
  display_name: string;
  source: "call" | "room";
  selected: boolean;
}

export interface ProtocolistRecipientsResponse {
  egress_id: string;
  room_id: string;
  policy: ProtocolistRecipientsPolicy;
  options: ProtocolistRecipientsOption[];
  owner: {
    user_id: string;
    display_name: string;
  } | null;
  self_user_id: string | null;
  candidates: ProtocolistRecipientsCandidate[];
}

export type SaveProtocolistRecipientsInput =
  | { mode: "owner" }
  | { mode: "all_participants" }
  | { mode: "self" }
  | { mode: "selected"; selected_user_ids: string[] };

/**
 * Сессии протоколиста больше нет: либо её остановили, либо она не начиналась.
 * Отдельный класс нужен, чтобы окно получателей показало человеку понятную
 * фразу, а не английский текст из исходников.
 */
export class ProtocolistSessionInactiveError extends Error {
  public constructor() {
    super("Protocolist session is not active");
    this.name = "ProtocolistSessionInactiveError";
  }
}

/**
 * Почему протоколиста не удалось позвать. Раньше причина жила только в тексте
 * брошенной ошибки и никуда не доходила: человек видел молчание, а в журнале
 * звонка не оставалось следа. Один и тот же код теперь идёт и в уведомление,
 * и в телеметрию, поэтому разбор жалобы не требует консоли разработчика.
 */
export type ProtocolistFailureReason =
  | "guest_forbidden"
  | "room_name_missing"
  | "backend_rejected"
  | "backend_unreachable";

/** Код причины в журнале звонка: с префиксом, чтобы не путать со сторонними. */
export const protocolistFailureReasonCode = (
  reason: ProtocolistFailureReason,
): string => `protocolist_${reason}`;

export type ProtocolistNotice =
  | { kind: "protocolist_connecting" }
  | { kind: "protocolist_connected" }
  | { kind: "protocolist_disconnected" }
  | { kind: "protocolist_failed"; reason?: ProtocolistFailureReason };

type ProtocolistSessionBase = {
  roomId: string;
  egressId: string;
};

type ProtocolistRichSessionBase = ProtocolistSessionBase & {
  startedAt: number | null;
  startedByUserId: string | null;
  startedByName: string | null;
  botUserId: string | null;
};

export type ProtocolistMachineState =
  | { kind: "idle" }
  | { kind: "starting"; requestRoomName: string }
  | ({ kind: "connecting" } & ProtocolistRichSessionBase)
  | ({
      kind: "connected";
      connectedAt: number | null;
    } & ProtocolistRichSessionBase)
  | ({ kind: "stopping" } & ProtocolistSessionBase);

export type ProtocolistViewState =
  | "idle"
  | "connecting"
  | "active"
  | "stopping";

export type ProtocolistButtonState =
  | { kind: "start"; disabled: boolean }
  | { kind: "connecting"; disabled: true }
  | { kind: "active_self_stop"; disabled: false }
  | { kind: "active_other_locked"; disabled: true }
  | { kind: "stopping"; disabled: true };

export interface UseProtocolistRecordingOptions {
  onNotice?: (notice: ProtocolistNotice) => void;
}

export interface UseProtocolistRecordingResult {
  machineState: ProtocolistMachineState;
  viewState: ProtocolistViewState;
  buttonState: ProtocolistButtonState;
  canConfigureRecipients: boolean;
  error: Error | null;
  errorNotice: ProtocolistNotice | null;
  shouldOpenRecipientsModal: boolean;
  recipients: ProtocolistRecipientsResponse | null;
  recipientsLoading: boolean;
  recipientsError: Error | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  loadRecipients: (egressId?: string) => Promise<ProtocolistRecipientsResponse>;
  saveRecipients: (
    input: SaveProtocolistRecipientsInput,
  ) => Promise<ProtocolistRecipientsResponse>;
  openRecipientsModal: () => void;
  consumeRecipientsModalRequest: () => void;
}

type StartProtocolistResponse = {
  egress_id?: unknown;
  room_id?: unknown;
  status?: unknown;
  protocolist_status?: unknown;
  started_at?: unknown;
  started_by?: unknown;
  started_by_name?: unknown;
  bot_user_id?: unknown;
};

type StopProtocolistResponse = {
  egress_id?: unknown;
};

type EgressInfoResponse = {
  is_recording?: unknown;
  egress_id?: unknown;
  room_id?: unknown;
  protocolist_status?: unknown;
  bot_user_id?: unknown;
  connected_at?: unknown;
  connection_error?: unknown;
  started_at?: unknown;
  started_by?: unknown;
  started_by_name?: unknown;
};

type ProtocolistInfoSnapshot =
  | {
      isRecording: false;
      egressId: null;
      roomId: string | null;
    }
  | ({
      isRecording: true;
      protocolistStatus: "connecting" | "connected";
      connectedAt: number | null;
      connectionError: string | null;
    } & ProtocolistRichSessionBase);

type ProtocolistMachineSignal =
  | { type: "start_requested"; requestRoomName: string }
  | { type: "start_succeeded"; data: StartProtocolistSuccessData }
  | { type: "start_failed" }
  | { type: "stop_requested"; egressId: string; roomId: string }
  | { type: "stop_succeeded" }
  | { type: "matrix_status"; data: ProtocolistStatusEventContent }
  | { type: "info_loaded"; data: ProtocolistInfoSnapshot }
  | { type: "session_conflict" };

type StartProtocolistSuccessData = ProtocolistRichSessionBase;

const PARTICIPANTS_INFO_DEBOUNCE_MS = 5000;

const parseNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const parseFiniteNumber = (value: unknown): number | null => {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const parseBoolean = (value: unknown): boolean => {
  return value === true || value === 1 || value === "1" || value === "true";
};

const getStateRoomId = (state: ProtocolistMachineState): string | null => {
  switch (state.kind) {
    case "connecting":
    case "connected":
    case "stopping":
      return state.roomId;
    default:
      return null;
  }
};

const getStateEgressId = (state: ProtocolistMachineState): string | null => {
  switch (state.kind) {
    case "connecting":
    case "connected":
    case "stopping":
      return state.egressId;
    default:
      return null;
  }
};

const makeRichSessionFromEvent = (
  eventContent: ProtocolistStatusEventContent,
  fallbackRoomId: string,
  fallbackState: ProtocolistMachineState,
): ProtocolistRichSessionBase | null => {
  const egressId =
    parseNonEmptyString(eventContent.egress_id) ??
    getStateEgressId(fallbackState);
  if (!egressId) {
    return null;
  }

  return {
    roomId: getStateRoomId(fallbackState) ?? fallbackRoomId,
    egressId,
    startedAt: parseFiniteNumber(eventContent.started_at),
    startedByUserId: parseNonEmptyString(eventContent.started_by_user_id),
    startedByName: parseNonEmptyString(eventContent.started_by_name),
    botUserId: parseNonEmptyString(eventContent.bot_user_id),
  };
};

export function transitionProtocolistState(
  state: ProtocolistMachineState,
  signal: ProtocolistMachineSignal,
  fallbackRoomId: string,
): ProtocolistMachineState {
  switch (signal.type) {
    case "start_requested":
      return {
        kind: "starting",
        requestRoomName: signal.requestRoomName,
      };
    case "start_succeeded":
      return {
        kind: "connecting",
        ...signal.data,
      };
    case "start_failed":
      return state.kind === "starting" ? { kind: "idle" } : state;
    case "stop_requested":
      return {
        kind: "stopping",
        egressId: signal.egressId,
        roomId: signal.roomId,
      };
    case "stop_succeeded":
    case "session_conflict":
      return { kind: "idle" };
    case "info_loaded": {
      if (!signal.data.isRecording) {
        if (
          state.kind === "connecting" ||
          state.kind === "connected" ||
          state.kind === "stopping"
        ) {
          return { kind: "idle" };
        }

        return state;
      }

      const currentEgressId = getStateEgressId(state);
      if (
        currentEgressId &&
        currentEgressId === signal.data.egressId &&
        state.kind !== "starting"
      ) {
        return state;
      }

      if (state.kind === "starting") {
        return state;
      }

      if (signal.data.protocolistStatus === "connected") {
        return {
          kind: "connected",
          roomId: signal.data.roomId,
          egressId: signal.data.egressId,
          startedAt: signal.data.startedAt,
          startedByUserId: signal.data.startedByUserId,
          startedByName: signal.data.startedByName,
          botUserId: signal.data.botUserId,
          connectedAt: signal.data.connectedAt,
        };
      }

      return {
        kind: "connecting",
        roomId: signal.data.roomId,
        egressId: signal.data.egressId,
        startedAt: signal.data.startedAt,
        startedByUserId: signal.data.startedByUserId,
        startedByName: signal.data.startedByName,
        botUserId: signal.data.botUserId,
      };
    }
    case "matrix_status": {
      switch (signal.data.status) {
        case "connecting": {
          const richState = makeRichSessionFromEvent(
            signal.data,
            fallbackRoomId,
            state,
          );
          return richState ? { kind: "connecting", ...richState } : state;
        }
        case "connected": {
          const richState = makeRichSessionFromEvent(
            signal.data,
            fallbackRoomId,
            state,
          );
          if (!richState) {
            return state;
          }

          return {
            kind: "connected",
            ...richState,
            connectedAt: parseFiniteNumber(signal.data.connected_at),
          };
        }
        case "failed":
          if (state.kind === "connected") {
            return state;
          }
          return { kind: "idle" };
        case "disconnected":
          return { kind: "idle" };
      }
      break;
    }
  }

  return state;
}

const makeInfoSnapshot = (
  data: EgressInfoResponse,
): ProtocolistInfoSnapshot => {
  const isRecording = parseBoolean(data.is_recording);
  const roomId = parseNonEmptyString(data.room_id);

  if (!isRecording) {
    return {
      isRecording: false,
      egressId: null,
      roomId,
    };
  }

  const egressId = parseNonEmptyString(data.egress_id);
  if (!egressId) {
    return {
      isRecording: false,
      egressId: null,
      roomId,
    };
  }

  const protocolistStatus =
    parseNonEmptyString(data.protocolist_status) === "connected"
      ? "connected"
      : "connecting";

  return {
    isRecording: true,
    protocolistStatus,
    roomId: roomId ?? "",
    egressId,
    startedAt: parseFiniteNumber(data.started_at),
    startedByUserId: parseNonEmptyString(data.started_by),
    startedByName: parseNonEmptyString(data.started_by_name),
    botUserId: parseNonEmptyString(data.bot_user_id),
    connectedAt: parseFiniteNumber(data.connected_at),
    connectionError: parseNonEmptyString(data.connection_error),
  };
};

const describeMachineState = (state: ProtocolistMachineState): string => {
  switch (state.kind) {
    case "idle":
      return "idle";
    case "starting":
      return `starting(room=${state.requestRoomName})`;
    case "connecting":
      return `connecting(egress=${state.egressId}, bot=${state.botUserId ?? "?"})`;
    case "connected":
      return `connected(egress=${state.egressId}, bot=${state.botUserId ?? "?"})`;
    case "stopping":
      return `stopping(egress=${state.egressId})`;
  }
};

const describeSignal = (signal: ProtocolistMachineSignal): string => {
  switch (signal.type) {
    case "start_requested":
      return `start_requested(room=${signal.requestRoomName})`;
    case "start_succeeded":
      return `start_succeeded(egress=${signal.data.egressId}, bot=${signal.data.botUserId ?? "?"})`;
    case "start_failed":
      return "start_failed";
    case "stop_requested":
      return `stop_requested(egress=${signal.egressId})`;
    case "stop_succeeded":
      return "stop_succeeded";
    case "session_conflict":
      return "session_conflict";
    case "info_loaded":
      return signal.data.isRecording
        ? `info_loaded(isRecording=true, status=${signal.data.protocolistStatus}, egress=${signal.data.egressId})`
        : "info_loaded(isRecording=false)";
    case "matrix_status":
      return `matrix_status(status=${signal.data.status}, egress=${parseNonEmptyString(signal.data.egress_id) ?? "?"})`;
  }
};

const isUserIdInCall = (
  userId: string | null,
  inCallUserIds: ReadonlySet<string>,
): boolean => {
  return !!userId && inCallUserIds.has(userId);
};

const getUserIdFromParticipantIdentity = (
  identity: string | undefined,
): string | null => {
  if (!identity) {
    return null;
  }

  const separatorIndex = identity.lastIndexOf(":");
  if (separatorIndex < 0) {
    return null;
  }

  const userId = identity.slice(0, separatorIndex);
  return userId.startsWith("@") ? userId : null;
};

function getButtonState(
  state: ProtocolistMachineState,
  currentUserId: string | null,
  inCallUserIds: ReadonlySet<string>,
  isGuestUser: boolean,
): ProtocolistButtonState {
  switch (state.kind) {
    case "idle":
      return { kind: "start", disabled: isGuestUser };
    case "starting":
    case "connecting":
      return { kind: "connecting", disabled: true };
    case "stopping":
      return { kind: "stopping", disabled: true };
    case "connected": {
      const startedBySelf =
        !!currentUserId && state.startedByUserId === currentUserId;
      const starterInCall = isUserIdInCall(
        state.startedByUserId,
        inCallUserIds,
      );
      const canStop = startedBySelf || !state.startedByUserId || !starterInCall;
      return canStop
        ? { kind: "active_self_stop", disabled: false }
        : { kind: "active_other_locked", disabled: true };
    }
  }
}

function getViewState(state: ProtocolistMachineState): ProtocolistViewState {
  switch (state.kind) {
    case "idle":
      return "idle";
    case "starting":
    case "connecting":
      return "connecting";
    case "stopping":
      return "stopping";
    case "connected":
      return "active";
  }
}

/**
 * Хук управления записью на протоколисте.
 */
export function useProtocolistRecording(
  room: Room,
  matrixRoom: MatrixRoom,
  options: UseProtocolistRecordingOptions = {},
): UseProtocolistRecordingResult {
  const cli = useClient();
  const participants = useParticipants({ room });
  const currentUserId = cli.client?.getUserId() ?? null;
  const isGuestUser = currentUserId?.startsWith("@guest_") ?? false;
  const onNoticeRef = useRef(options.onNotice);
  onNoticeRef.current = options.onNotice;

  const [machineState, setMachineState] = useState<ProtocolistMachineState>({
    kind: "idle",
  });
  const [error, setError] = useState<Error | null>(null);
  const [errorNotice, setErrorNotice] = useState<ProtocolistNotice | null>(
    null,
  );
  const [shouldOpenRecipientsModal, setShouldOpenRecipientsModal] =
    useState(false);
  const [recipients, setRecipients] =
    useState<ProtocolistRecipientsResponse | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState<Error | null>(null);

  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;
  const previousMachineStateKindRef =
    useRef<ProtocolistMachineState["kind"]>("idle");

  const fallbackRoomId = matrixRoom.roomId;
  const backendBaseUrl = `https://${getHomeserverAssociatedDomain(
    cli.client?.getDomain() ?? "",
    "backend",
  )}`;

  const inCallUserIds = useMemo(() => {
    const userIds = new Set<string>();
    if (currentUserId) {
      userIds.add(currentUserId);
    }
    participants.forEach((participant) => {
      const userId = getUserIdFromParticipantIdentity(participant.identity);
      if (userId) {
        userIds.add(userId);
      }
    });
    return userIds;
  }, [currentUserId, participants]);

  const participantSignature = useMemo(
    () =>
      participants
        .map((participant) => participant.identity ?? participant.sid)
        .sort()
        .join("|"),
    [participants],
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    const accessToken = (await cli.client?.getOpenIdToken())?.access_token;
    if (!accessToken) {
      throw new Error("OpenID access token not found");
    }
    return `Bearer ${accessToken}`;
  }, [cli.client]);

  const applySignal = useCallback(
    (signal: ProtocolistMachineSignal): void => {
      setMachineState((previousState) => {
        const nextState = transitionProtocolistState(
          previousState,
          signal,
          fallbackRoomId,
        );
        logger.info(
          `[Protocolist] Переход состояния: ${describeMachineState(previousState)} -> ${describeMachineState(nextState)}; причина=${describeSignal(signal)}`,
        );
        if (
          describeMachineState(previousState) !==
          describeMachineState(nextState)
        ) {
          callTraceJournal.record({
            eventType: "protocolist.state_changed",
            stage: "protocolist",
            outcome: "state_changed",
            payload: {
              previous: previousState.kind,
              current: nextState.kind,
              signal: signal.type,
            },
          });
        }
        if (
          signal.type === "matrix_status" &&
          signal.data.status === "failed"
        ) {
          callTraceJournal.record({
            eventType: "protocolist.failed",
            stage: "protocolist",
            severity: "error",
            outcome: "failed",
            reasonCode: "matrix_status_failed",
            payload: {
              operation: "matrix_status_transition",
              previous: previousState.kind,
              current: nextState.kind,
              signal: "matrix_status",
            },
          });
        }
        return nextState;
      });
    },
    [fallbackRoomId],
  );

  const loadInfo =
    useCallback(async (): Promise<ProtocolistInfoSnapshot | null> => {
      try {
        const roomIdForRequest = parseNonEmptyString(room.name);
        if (!roomIdForRequest) {
          return null;
        }

        const response = await fetch(
          `${backendBaseUrl}/egress/info?room_id=${encodeURIComponent(roomIdForRequest)}`,
          {
            headers: {
              Authorization: await getAccessToken(),
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Protocolist info failed: ${response.statusText}`);
        }

        const data = (await response.json()) as EgressInfoResponse;
        const snapshot = makeInfoSnapshot(data);
        logger.info(
          snapshot.isRecording
            ? `[Protocolist] Получен /egress/info: isRecording=true, status=${snapshot.protocolistStatus}, egress=${snapshot.egressId}, bot=${snapshot.botUserId ?? "?"}, connectedAt=${snapshot.connectedAt ?? "?"}`
            : `[Protocolist] Получен /egress/info: isRecording=false, room=${snapshot.roomId ?? "?"}`,
        );
        applySignal({ type: "info_loaded", data: snapshot });
        return snapshot;
      } catch (err) {
        logger.warn("[Protocolist] Не удалось получить /egress/info", err);
        return null;
      }
    }, [applySignal, backendBaseUrl, getAccessToken, room.name]);

  const getCurrentProtocolistStateEvent =
    useCallback((): MatrixEvent | null => {
      const event = matrixRoom.currentState.getStateEvents(
        ProtocolistStatusEventType,
        ProtocolistStatusStateKey,
      );

      if (Array.isArray(event)) {
        return event.at(-1) ?? null;
      }

      return event ?? null;
    }, [matrixRoom.currentState]);

  const applyMatrixStatusEvent = useCallback(
    async (event: MatrixEvent): Promise<void> => {
      if (event.getRoomId() !== matrixRoom.roomId) {
        return;
      }

      if (
        event.getType() !== ProtocolistStatusEventType ||
        event.getStateKey() !== ProtocolistStatusStateKey
      ) {
        return;
      }

      const matrixClient = cli.client;
      if (!matrixClient) {
        return;
      }

      await matrixClient.decryptEventIfNeeded(event).catch(() => {});
      if (event.isBeingDecrypted() || event.isDecryptionFailure()) {
        return;
      }

      const content = event.getContent();
      if (!isProtocolistStatusEventContent(content)) {
        return;
      }

      applySignal({
        type: "matrix_status",
        data: content,
      });

      if (content.status === "failed") {
        setErrorNotice({ kind: "protocolist_failed" });
        onNoticeRef.current?.({ kind: "protocolist_failed" });
      }
    },
    [applySignal, cli.client, matrixRoom.roomId],
  );

  const getRecipientsUrl = useCallback(
    (egressId: string): string => {
      return `${backendBaseUrl}/egress/${encodeURIComponent(egressId)}/recipients`;
    },
    [backendBaseUrl],
  );

  const loadRecipients = useCallback(
    async (egressId?: string): Promise<ProtocolistRecipientsResponse> => {
      // Идентификатор сессии можно передать напрямую: сразу после успешного
      // старта состояние машины ещё не перерисовано, и чтение из неё вернуло бы
      // пустоту — окно получателей открывалось с ошибкой на ровном месте.
      const currentEgressId =
        egressId ?? getStateEgressId(machineStateRef.current);
      if (!currentEgressId) {
        throw new ProtocolistSessionInactiveError();
      }

      setRecipientsLoading(true);
      setRecipientsError(null);

      try {
        const response = await fetch(getRecipientsUrl(currentEgressId), {
          headers: {
            Authorization: await getAccessToken(),
          },
        });

        if (response.status === 409) {
          applySignal({ type: "session_conflict" });
          setRecipients(null);
          throw new ProtocolistSessionInactiveError();
        }

        if (!response.ok) {
          throw new Error(
            `Protocolist recipients failed: ${response.statusText}`,
          );
        }

        const data = (await response.json()) as ProtocolistRecipientsResponse;
        setRecipients(data);
        return data;
      } catch (err) {
        const nextError =
          err instanceof Error
            ? err
            : new Error("Failed to load protocolist recipients");
        setRecipientsError(nextError);
        throw nextError;
      } finally {
        setRecipientsLoading(false);
      }
    },
    [applySignal, getAccessToken, getRecipientsUrl],
  );

  const saveRecipients = useCallback(
    async (
      input: SaveProtocolistRecipientsInput,
    ): Promise<ProtocolistRecipientsResponse> => {
      const currentEgressId = getStateEgressId(machineStateRef.current);
      if (!currentEgressId) {
        throw new Error("Protocolist session is not active");
      }

      if (
        input.mode === "selected" &&
        (!input.selected_user_ids || input.selected_user_ids.length === 0)
      ) {
        throw new Error("At least one selected recipient is required");
      }

      setRecipientsLoading(true);
      setRecipientsError(null);

      try {
        const response = await fetch(getRecipientsUrl(currentEgressId), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: await getAccessToken(),
          },
          body: JSON.stringify(input),
        });

        if (response.status === 409) {
          applySignal({ type: "session_conflict" });
          setRecipients(null);
          throw new Error("Protocolist session has already finished");
        }

        if (!response.ok) {
          throw new Error(
            `Protocolist recipients save failed: ${response.statusText}`,
          );
        }

        const data = (await response.json()) as ProtocolistRecipientsResponse;
        setRecipients(data);
        return data;
      } catch (err) {
        const nextError =
          err instanceof Error
            ? err
            : new Error("Failed to save protocolist recipients");
        setRecipientsError(nextError);
        throw nextError;
      } finally {
        setRecipientsLoading(false);
      }
    },
    [applySignal, getAccessToken, getRecipientsUrl],
  );

  /**
   * Единственный выход для любого отказа при старте: и запись в журнал звонка,
   * и видимое человеку уведомление. Прежде часть отказов уходила только в
   * брошенную ошибку, а вызывающий код её гасил — нажатие выглядело как
   * несработавшая кнопка.
   */
  const reportStartFailure = useCallback(
    (
      reason: ProtocolistFailureReason,
      failure: Error,
      relatedEventId: string | null = null,
      durationMs: number | null = null,
    ): void => {
      callTraceJournal.record({
        eventType: "protocolist.request_failed",
        stage: "protocolist",
        severity: "error",
        outcome: "failed",
        reasonCode: protocolistFailureReasonCode(reason),
        relatedEventId,
        durationMs,
        payload: describeTraceError(failure),
      });
      setError(failure);
      setErrorNotice({ kind: "protocolist_failed", reason });
      onNoticeRef.current?.({ kind: "protocolist_failed", reason });
    },
    [],
  );

  const start = useCallback(async (): Promise<void> => {
    setError(null);
    setErrorNotice(null);

    // Эти две проверки раньше стояли до try/catch и выбрасывали ошибку молча:
    // ни уведомления, ни события в журнале. Именно так выглядел случай, когда
    // человек трижды нажал «Позвать протоколиста» и на сервер не ушло ничего.
    if (isGuestUser) {
      const guestFailure = new Error(
        "Guest users cannot start protocolist recording",
      );
      reportStartFailure("guest_forbidden", guestFailure);
      throw guestFailure;
    }

    const requestRoomName = parseNonEmptyString(room.name);
    if (!requestRoomName) {
      const roomNameFailure = new Error(
        "Protocolist room name is not available",
      );
      reportStartFailure("room_name_missing", roomNameFailure);
      throw roomNameFailure;
    }

    const startEventId = callTraceJournal.record({
      eventType: "protocolist.start_requested",
      stage: "protocolist",
      outcome: "started",
    });
    const startedAt = performance.now();
    applySignal({
      type: "start_requested",
      requestRoomName,
    });

    try {
      const response = await fetch(`${backendBaseUrl}/egress/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: await getAccessToken(),
        },
        body: JSON.stringify({
          room_name: requestRoomName,
        }),
      });

      if (!response.ok) {
        throw new Error(`Protocolist start failed: ${response.statusText}`);
      }

      const data = (await response.json()) as StartProtocolistResponse;
      const egressId = parseNonEmptyString(data.egress_id);
      if (!egressId) {
        throw new Error("Protocolist start response has no egress_id");
      }

      const nextState: StartProtocolistSuccessData = {
        roomId: parseNonEmptyString(data.room_id) ?? fallbackRoomId,
        egressId,
        startedAt: parseFiniteNumber(data.started_at),
        startedByUserId: parseNonEmptyString(data.started_by),
        startedByName: parseNonEmptyString(data.started_by_name),
        botUserId: parseNonEmptyString(data.bot_user_id),
      };

      applySignal({
        type: "start_succeeded",
        data: nextState,
      });
      callTraceJournal.record({
        eventType: "protocolist.request_succeeded",
        stage: "protocolist",
        outcome: "succeeded",
        relatedEventId: startEventId,
        durationMs: Math.round(performance.now() - startedAt),
      });

      setShouldOpenRecipientsModal(true);
      // Идентификатор передаём явно: состояние машины обновится только к
      // следующей отрисовке, и запрос ушёл бы с пустой сессией.
      void loadRecipients(egressId).catch((err) => {
        logger.warn(
          "[Protocolist] Не удалось загрузить получателей после старта",
          err,
        );
      });
    } catch (err) {
      applySignal({ type: "start_failed" });
      const nextError =
        err instanceof Error ? err : new Error("Failed to start protocolist");
      // fetch бросает TypeError только когда запрос вообще не ушёл. Человеку
      // это разные беды: «сервер отказал» он перескажет как ошибку, а «до
      // сервера не достучались» — как пропавшую сеть, и мы не будем гадать.
      const reason: ProtocolistFailureReason =
        err instanceof TypeError || !navigator.onLine
          ? "backend_unreachable"
          : "backend_rejected";
      // Без этого отказ бэкенда был не виден: пункт меню молча возвращался в
      // «Позвать протоколиста», и человек думал, что просто не нажалось.
      reportStartFailure(
        reason,
        nextError,
        startEventId,
        Math.round(performance.now() - startedAt),
      );
      throw nextError;
    }
  }, [
    applySignal,
    backendBaseUrl,
    fallbackRoomId,
    getAccessToken,
    isGuestUser,
    loadRecipients,
    reportStartFailure,
    room.name,
  ]);

  const stop = useCallback(async (): Promise<void> => {
    const currentState = machineStateRef.current;
    const currentEgressId = getStateEgressId(currentState);
    const currentRoomId = getStateRoomId(currentState);

    if (!currentEgressId || !currentRoomId) {
      return;
    }

    applySignal({
      type: "stop_requested",
      egressId: currentEgressId,
      roomId: currentRoomId,
    });
    const stopEventId = callTraceJournal.record({
      eventType: "protocolist.stop_requested",
      stage: "protocolist",
      outcome: "started",
    });
    const stoppedAt = performance.now();
    setError(null);

    try {
      const response = await fetch(`${backendBaseUrl}/egress/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: await getAccessToken(),
        },
        body: JSON.stringify({
          egress_id: currentEgressId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Protocolist stop failed: ${response.statusText}`);
      }

      await response.json().catch(() => ({}) as StopProtocolistResponse);
      applySignal({ type: "stop_succeeded" });
      callTraceJournal.record({
        eventType: "protocolist.stop_succeeded",
        stage: "protocolist",
        outcome: "succeeded",
        relatedEventId: stopEventId,
        durationMs: Math.round(performance.now() - stoppedAt),
      });
      setRecipients(null);
    } catch (err) {
      callTraceJournal.record({
        eventType: "protocolist.stop_failed",
        stage: "protocolist",
        severity: "error",
        outcome: "failed",
        relatedEventId: stopEventId,
        durationMs: Math.round(performance.now() - stoppedAt),
        payload: describeTraceError(err),
      });
      const nextError =
        err instanceof Error ? err : new Error("Failed to stop protocolist");
      setError(nextError);
      const latestInfo = await loadInfo();
      if (!latestInfo) {
        applySignal({ type: "session_conflict" });
      }
      throw nextError;
    }
  }, [applySignal, backendBaseUrl, getAccessToken, loadInfo]);

  const consumeRecipientsModalRequest = useCallback((): void => {
    setShouldOpenRecipientsModal(false);
  }, []);

  const openRecipientsModal = useCallback((): void => {
    setShouldOpenRecipientsModal(true);
  }, []);

  useEffect(() => {
    const previousKind = previousMachineStateKindRef.current;
    if (
      machineState.kind === "connecting" &&
      previousKind !== "connecting" &&
      previousKind !== "connected"
    ) {
      onNoticeRef.current?.({ kind: "protocolist_connecting" });
    }
    if (machineState.kind === "connected" && previousKind !== "connected") {
      onNoticeRef.current?.({ kind: "protocolist_connected" });
    }
    if (
      machineState.kind === "idle" &&
      (previousKind === "connected" || previousKind === "stopping")
    ) {
      onNoticeRef.current?.({ kind: "protocolist_disconnected" });
    }

    previousMachineStateKindRef.current = machineState.kind;
  }, [machineState.kind]);

  useEffect(() => {
    const currentEvent = getCurrentProtocolistStateEvent();
    if (!currentEvent) {
      void loadInfo();
      return;
    }

    void applyMatrixStatusEvent(currentEvent).finally(() => {
      void loadInfo();
    });
  }, [applyMatrixStatusEvent, getCurrentProtocolistStateEvent, loadInfo]);

  useEffect(() => {
    const matrixClient = cli.client;
    if (!matrixClient) {
      return;
    }

    const onStateEvent = (
      event: MatrixEvent,
      _state: unknown,
      _prevEvent: MatrixEvent | null,
    ): void => {
      void applyMatrixStatusEvent(event);
    };

    const onDecryptedEvent = (event: MatrixEvent): void => {
      void applyMatrixStatusEvent(event);
    };

    matrixRoom.currentState.on(RoomStateEvent.Events, onStateEvent);
    matrixClient.on(MatrixEventEvent.Decrypted, onDecryptedEvent);

    return (): void => {
      matrixRoom.currentState.off(RoomStateEvent.Events, onStateEvent);
      matrixClient.off(MatrixEventEvent.Decrypted, onDecryptedEvent);
    };
  }, [applyMatrixStatusEvent, cli.client, matrixRoom.currentState]);

  useEffect(() => {
    logger.info(
      `[Protocolist] Планируем перепроверку /egress/info через ${PARTICIPANTS_INFO_DEBOUNCE_MS}ms после изменения participants`,
    );
    const timeoutId = window.setTimeout(() => {
      logger.info("[Protocolist] Запускаем debounce-перепроверку /egress/info");
      void loadInfo();
    }, PARTICIPANTS_INFO_DEBOUNCE_MS);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [loadInfo, participantSignature]);

  const buttonState = useMemo(
    () =>
      getButtonState(machineState, currentUserId, inCallUserIds, isGuestUser),
    [currentUserId, inCallUserIds, isGuestUser, machineState],
  );
  const viewState = useMemo(() => getViewState(machineState), [machineState]);
  const canConfigureRecipients = useMemo(() => {
    if (!currentUserId) {
      return false;
    }

    switch (machineState.kind) {
      case "connecting":
      case "connected":
        return machineState.startedByUserId === currentUserId;
      default:
        return false;
    }
  }, [currentUserId, machineState]);

  return {
    machineState,
    viewState,
    buttonState,
    canConfigureRecipients,
    error,
    errorNotice,
    shouldOpenRecipientsModal,
    recipients,
    recipientsLoading,
    recipientsError,
    start,
    stop,
    loadRecipients,
    saveRecipients,
    openRecipientsModal,
    consumeRecipientsModalRequest,
  };
}
